import DevJSONView from '@/components/DevJSONView';
import { showToast } from '@/features/toast/toastSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { DataGrid } from '@mui/x-data-grid';
import React, { useMemo } from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { NoRowsErrorOverlay, NoRowsOverlay } from '../../table/DataGridComponents/NoRowsOverlay';
import { Box, Button, Stack, Switch, Tooltip, Typography, useTheme } from '@mui/material';
import {
	useBatchSaveFormConfigMutation,
	useGetFormConfigQuery,
	useSaveFormConfigMutation,
} from '@/features/config/configApi';
import DataGridNumericFormat from '@/components/table/DataGridComponents/DataGridNumericFormat';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import DataGridMultilinePopper from '@/components/table/DataGridComponents/DataGridMultilinePopper';
import ShareDialog from '@/components/dialogs/ShareDialog';
import { openDialog } from '@/features/dialog/dialogSlice';
import DataGridMultiBrokerGroup from '@/components/table/DataGridComponents/DataGridMultiBrokerGroup';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';

const CellSwitch = ({ disabled, checked, onChange, tooltipTitle }) => {
	return (
		<Tooltip title={tooltipTitle}>
			<div>
				<Switch
					size='small'
					disabled={disabled}
					checked={checked}
					onChange={onChange}
					slotProps={{ input: { 'aria-label': 'controlled' } }}
				/>
			</div>
		</Tooltip>
	);
};

const CONFIG_TYPE = 'client';

const ClientConfigTable = () => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const [rowSelectionModel, setRowSelectionModel] = useState([]);
	const [sortModel, setSortModel] = useState([
		{
			field: 'group',
			sort: 'desc',
		},
	]);

	const [selectedRow, setSelectedRow] = useState(null);

	const showShareDialog = useSelector((state) => state.dialog.open?.share);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const canViewGroup = userDetails?.canViewGroup;

	const columnVisibilityModel = useMemo(
		() => ({
			...(!canViewGroup && { brokerGroupIds: false }),
		}),
		[canViewGroup],
	);

	const { getFeatureFlags } = useFeatureFlagContext();
	const editConfigTable = Boolean(getFeatureFlags('editConfigTable')?.active);

	const configQuery = useGetFormConfigQuery({ configType: CONFIG_TYPE }, { skip: !userDetails });

	const rows = configQuery.data ?? [];

	const [saveConfig, { isLoading: isUpdateLoading }] = useSaveFormConfigMutation();
	const [batchSaveConfig, { isLoading: isBatchUpdateLoading }] = useBatchSaveFormConfigMutation();

	const handleShare = async (brokerGroupIds, shareWithLedger) => {
		console.log('handleShare', { selectedRow, brokerGroupIds, shareWithLedger, rows });
		if (selectedRow) {
			return await handleSave({
				id: selectedRow.id,
				configType: selectedRow.configType,
				brokerGroupIds,
			});
		}

		const docIds = rows.map((r) => r.id);
		return await batchSaveConfig({
			docIds,
			properties: {
				brokerGroupIds,
				...(shareWithLedger != null && {
					ledger: shareWithLedger ? userDetails?.broker?.ledger : '',
				}),
			},
			configType: CONFIG_TYPE,
		})
			.unwrap()
			.then((res) => {
				console.log('Share config res', res);
				dispatch(
					showToast({
						message: 'Successfully shared config',
						success: true,
					}),
				);
				return true;
			})
			.catch((e) => {
				console.error(`Error updating config ${docIds}`, e);
				dispatch(
					showToast({
						message: 'Error sharing config; please try again.',
						error: true,
					}),
				);
				return false;
			});
	};

	const handleSave = async ({ id, configType, ...properties }) => {
		console.log('Saving config doc', id, properties);
		if (Object.keys(properties ?? {}).length === 0) return;
		return await saveConfig({
			docId: id,
			properties,
			configType,
		})
			.unwrap()
			.then((res) => {
				console.log('Update config res', res);
				dispatch(
					showToast({
						message: 'Successfully updated config',
						success: true,
					}),
				);
				return true;
			})
			.catch((e) => {
				console.error(`Error updating config ${id}`, e);
				dispatch(
					showToast({
						message: 'Error updating config; please try again.',
						error: true,
					}),
				);
				return false;
			});
	};

	const disabled = isUpdateLoading || isBatchUpdateLoading;
	const isLoading = configQuery.isLoading || disabled;

	const columns = [
		{
			field: 'label',
			headerName: 'Label',
			flex: 2,
			minWidth: 200,
			editable: true,
			sortable: true,
			renderHeader: (params) => {
				return (
					<Tooltip title='The label used in the Create Client form'>
						<Typography variant='inherit' className='MuiDataGrid-columnHeader-Title'>
							{params.colDef.headerName}
						</Typography>
					</Tooltip>
				);
			},
			renderCell: ({ row }) => {
				return (
					<Tooltip title={row.description}>
						<span>{row.label}</span>
					</Tooltip>
				);
			},
			display: 'flex',
		},
		{
			field: 'mandatory.client',
			headerName: 'Required (client)',
			flex: 0.8,
			minWidth: 100,
			editable: false,
			sortable: false,
			display: 'flex',
			renderHeader: (params) => {
				return (
					<Tooltip title='Whether or not this field is mandatory for clients. Some fields are required by Insight as a default and cannot be changed.'>
						<Stack>
							<Typography
								variant='inherit'
								className='MuiDataGrid-columnHeader-Title'
							>
								{'Required'}
							</Typography>
							<Typography variant='task_updated'>{'Client'}</Typography>
						</Stack>
					</Tooltip>
				);
			},
			renderCell: (params) => {
				const { row } = params;
				const disabled =
					!editConfigTable || (row?.mandatory?.client && !row.optional) || isLoading;
				if (row?.name === 'CurrentStatusId') return <></>;

				return (
					<CellSwitch
						disabled={disabled}
						checked={row?.mandatory?.client}
						onChange={async (e) => {
							const checked = e.target.checked;
							const mandatory = {
								...row.mandatory,
								client: e.target.checked,
							};

							const success = await handleSave({
								id: params.row.id,
								mandatory,
								...(checked && {
									visible: {
										...row.visible,
										create: true,
									},
								}),
								configType: row.configType,
							});
						}}
						tooltipTitle={
							disabled
								? 'This field is required by default and cannot be changed'
								: ''
						}
					/>
				);
			},
		},
		{
			field: 'mandatory.prospect',
			headerName: 'Required (prospect)',
			flex: 0.8,
			minWidth: 100,
			editable: false,
			sortable: false,
			display: 'flex',
			renderHeader: (params) => {
				return (
					<Tooltip title='Whether or not this field is mandatory for prospects. Some fields are required by Insight as a default and cannot be changed.'>
						<Stack>
							<Typography
								variant='inherit'
								className='MuiDataGrid-columnHeader-Title'
							>
								{'Required'}
							</Typography>
							<Typography variant='task_updated'>{'Prospect'}</Typography>
						</Stack>
					</Tooltip>
				);
			},
			renderCell: (params) => {
				const { row } = params;
				const disabled =
					!editConfigTable || (row?.mandatory?.prospect && !row.optional) || isLoading;
				if (row?.name === 'CurrentStatusId') return <></>;
				return (
					<CellSwitch
						disabled={disabled}
						checked={row?.mandatory?.prospect}
						onChange={async (e) => {
							const checked = e.target.checked;
							const mandatory = {
								...row.mandatory,
								prospect: checked,
							};
							const success = await handleSave({
								id: row.id,
								mandatory,
								...(checked && {
									visible: {
										...row.visible,
										prospect: true,
									},
								}),
								configType: row.configType,
							});
						}}
						tooltipTitle={
							disabled
								? 'This field is required by default and cannot be changed'
								: ''
						}
					/>
				);
			},
		},
		{
			field: 'visible.create',
			headerName: 'Visible (create)',
			flex: 0.8,
			minWidth: 100,
			editable: false,
			sortable: false,
			display: 'flex',
			renderHeader: (params) => {
				return (
					<Tooltip title='Whether this field is visible in the Create Client form'>
						<Stack>
							<Typography
								variant='inherit'
								className='MuiDataGrid-columnHeader-Title'
							>
								{'Visible'}
							</Typography>
							<Typography variant='task_updated'>{'Create client'}</Typography>
						</Stack>
					</Tooltip>
				);
			},
			renderCell: (params) => {
				const { row } = params;
				const disabled = !editConfigTable || row?.mandatory?.client || isLoading;
				if (row?.name === 'CurrentStatusId') return <></>;
				return (
					<CellSwitch
						disabled={disabled}
						checked={row?.visible?.create}
						onChange={async (e) => {
							const visible = {
								...row.visible,
								create: e.target.checked,
							};
							const success = await handleSave({
								id: row.id,
								visible,
								configType: row.configType,
							});
						}}
						tooltipTitle={
							disabled
								? 'To change the visibility of this field, set it to non-required for Client'
								: ''
						}
					/>
				);
			},
		},
		{
			field: 'visible.prospect',
			headerName: 'Visible (prospect)',
			flex: 0.8,
			minWidth: 100,
			editable: false,
			sortable: false,
			display: 'flex',
			renderHeader: (params) => {
				return (
					<Tooltip title='Whether this field is visible in the Create/Update Client form for prospects'>
						<Stack>
							<Typography
								variant='inherit'
								className='MuiDataGrid-columnHeader-Title'
							>
								{'Visible'}
							</Typography>
							<Typography variant='task_updated'>{'Prospect'}</Typography>
						</Stack>
					</Tooltip>
				);
			},
			renderCell: (params) => {
				const { row } = params;
				const disabled = !editConfigTable || row?.mandatory?.prospect || isLoading;
				return (
					<CellSwitch
						disabled={disabled}
						checked={row?.visible?.prospect}
						onChange={async (e) => {
							const visible = {
								...row.visible,
								prospect: e.target.checked,
							};
							const success = await handleSave({
								id: row.id,
								visible,
								configType: row.configType,
							});
						}}
						tooltipTitle={
							disabled
								? 'To change the visibility of this field, set it to non-required for Prospect'
								: ''
						}
					/>
				);
			},
		},
		{
			field: 'visible.edit',
			headerName: 'Visible (edit)',
			flex: 0.8,
			minWidth: 100,
			editable: false,
			sortable: false,
			display: 'flex',
			renderHeader: (params) => {
				return (
					<Tooltip title='Whether this field is visible in the Client Details section'>
						<Stack>
							<Typography
								variant='inherit'
								className='MuiDataGrid-columnHeader-Title'
							>
								{'Visible'}
							</Typography>
							<Typography variant='task_updated'>{'Update client'}</Typography>
						</Stack>
					</Tooltip>
				);
			},
			renderCell: (params) => {
				const { row } = params;

				return (
					<Switch
						size='small'
						disabled={!editConfigTable}
						checked={row?.visible?.update}
						onChange={async (e) => {
							const visible = {
								...row.visible,
								update: e.target.checked,
							};
							const success = await handleSave({
								id: row.id,
								visible,
								configType: row.configType,
							});
						}}
						slotProps={{ input: { 'aria-label': 'controlled' } }}
					/>
				);
			},
		},
		{
			field: 'group',
			headerName: 'Group',
			flex: 1.2,
			minWidth: 120,
			editable: false,
			sortable: true,
			display: 'flex',
			renderHeader: (params) => {
				return (
					<Tooltip title='Used to group similar fields together'>
						<Typography variant='inherit' className='MuiDataGrid-columnHeader-Title'>
							{params.colDef.headerName}
						</Typography>
					</Tooltip>
				);
			},
		},
		// {
		// 	field: 'order',
		// 	headerName: 'Order',
		// 	flex: 0.5,
		// 	minWidth: 50,
		// 	editable: true,
		// 	sortable: true,
		// 	display: 'flex',
		// 	renderHeader: (params) => {
		// 		return (
		// 			<Tooltip title='Which order to render this field within its Group'>
		// 				<Typography variant='inherit' className='MuiDataGrid-columnHeader-Title'>
		// 					{params.colDef.headerName}
		// 				</Typography>
		// 			</Tooltip>
		// 		);
		// 	},
		// 	renderEditCell: (params) => <DataGridNumericFormat {...params} />,
		// },
		{
			field: 'name',
			headerName: 'Field Name',
			flex: 2,
			minWidth: 200,
			editable: false,
			sortable: true,
			renderHeader: (params) => {
				return (
					<Tooltip title='The internal name of the field in Insight or Firebase'>
						<Typography variant='inherit' className='MuiDataGrid-columnHeader-Title'>
							{params.colDef.headerName}
						</Typography>
					</Tooltip>
				);
			},
			renderCell: ({ row }) => {
				return (
					<Tooltip title={row.description}>
						<span>{row.name}</span>
					</Tooltip>
				);
			},
			display: 'flex',
		},
		{
			field: 'helperText',
			headerName: 'Helper Text',
			flex: 2,
			minWidth: 200,
			editable: true,
			sortable: false,
			renderHeader: (params) => {
				return (
					<Tooltip title='The helper text the user sees; leave blank to not display helper text'>
						<Typography variant='inherit' className='MuiDataGrid-columnHeader-Title'>
							{params.colDef.headerName}
						</Typography>
					</Tooltip>
				);
			},
			renderEditCell: (params) => {
				return <DataGridMultilinePopper {...params} newLine={false} />;
			},
			display: 'flex',
		},
		{
			field: 'brokerGroupIds',
			headerName: 'Shared with',
			flex: 1,
			minWidth: 200,
			editable: false,
			sortable: false,
			display: 'flex',
			renderHeader: (params) => {
				return (
					<Tooltip title='Who this row is shared with'>
						<Typography variant='inherit' className='MuiDataGrid-columnHeader-Title'>
							{params.colDef.headerName}
						</Typography>
					</Tooltip>
				);
			},
			renderCell: (params) => {
				const { row } = params;
				const brokerGroupIds = row.brokerGroupIds;
				const ledger = row.ledger;

				return ledger || row.default ? (
					<Typography
						{...(row.default && { sx: { color: theme.palette.text.disabled, ml: 1 } })}
					>
						{ledger ? 'Shared with ledger' : 'Default'}
					</Typography>
				) : (
					<DataGridMultiBrokerGroup
						brokerGroupIds={brokerGroupIds}
						showAdmin={userDetails?.isFolioAdmin}
						onClick={() => {
							dispatch(
								openDialog({
									dialogName: 'share',
									data: { ...row },
								}),
							);
						}}
						sx={{ cursor: 'pointer' }}
					/>
				);
			},
		},
	];

	return (
		<Stack
			direction='column'
			spacing={1}
			sx={{ height: '75vh', maxHeight: '100%' }}
			// onClick={() => {
			// 	if (!showDeleteDialog && !showShareDialog) {
			// 		setRowSelectionModel([]);
			// 		setSelectedRow(null);
			// 	}
			// }}
		>
			{canViewGroup && (
				<Stack direction='row' sx={{ justifyContent: 'flex-end', p: 1 }}>
					<Box>
						<Button
							variant='table_actions'
							disabled={isLoading}
							onClick={(e) => {
								e.stopPropagation();

								setRowSelectionModel([]);
								setSelectedRow(null);

								const brokerGroupIds = Array.from(
									new Set(
										rows.flatMap((r) => r.brokerGroupIds ?? []).filter(Boolean),
									),
								);

								const sharedWithLedger = rows.every(
									(r) => r.ledger === userDetails?.broker?.ledger,
								);

								dispatch(
									openDialog({
										dialogName: 'share',
										data: { brokerGroupIds, ledger: sharedWithLedger },
									}),
								);
							}}
						>
							Share config
						</Button>
					</Box>
				</Stack>
			)}

			<DataGrid
				sx={{
					'& .MuiDataGrid-editInputCell': {
						backgroundColor: 'transparent',
					},
					'--DataGrid-overlayHeight': '3em',
					'& .MuiDataGrid-cell': {
						padding: 1,
						minHeight: '42px',
					},
					'& .Mui-disabled .MuiDataGrid-cell': {
						backgroundColor: theme.palette.action.disabledBackground,
						color: theme.palette.text.disabled,
					},
					'& .Mui-disabled .MuiDataGrid-row': {
						backgroundColor: 'transparent',
					},
					border: 'none',
				}}
				getRowClassName={(params) => (disabled ? 'Mui-disabled' : '')}
				columnVisibilityModel={columnVisibilityModel}
				loading={configQuery.isLoading}
				width='100%'
				getRowHeight={() => 'auto'}
				processRowUpdate={async (newRow, oldRow) => {
					console.log('processRowUpdate', { newRow, oldRow });
					if (newRow.order !== oldRow.order) {
						const amount = rows.filter((r) => r.group === oldRow.group).length;
						newRow.order = Math.min(amount, Math.max(newRow.order, 0));
					}
					const { configType, group } = newRow;
					const update = Object.entries(newRow).reduce(
						(acc, [key, value]) => {
							if (oldRow[key] !== value && value != null) {
								acc[key] = value;
							}
							return acc;
						},
						{ id: oldRow.id, configType, group },
					);
					const success = await handleSave(update);
					return success ? newRow : oldRow;
					// return newRow;
				}}
				rows={rows}
				columns={columns}
				isCellEditable={(params) => {
					if (!editConfigTable || isLoading) return false;
					return params.colDef.editable;
					// if (!params.colDef.editable) return false;
					// return false;
				}}
				onRowSelectionModelChange={(model) => {
					const rowId = model?.[0];
					setRowSelectionModel(model);
					setSelectedRow(rows.find((r) => r.id === rowId));
				}}
				initialState={{
					sorting: {
						sortModel,
					},
				}}
				sortingOrder={['desc', 'asc']}
				rowSelectionModel={rowSelectionModel}
				disableColumnResize
				disableColumnFilter
				disableColumnSelector
				disableColumnMenu
				onRowClick={(_, e) => {
					e.stopPropagation();
				}}
				hideFooter
				slots={{
					noRowsOverlay: configQuery.isError
						? () => <NoRowsErrorOverlay objectType={'client config documents'} />
						: () => <NoRowsOverlay objectType={'client config documents'} />,
				}}
				sortModel={sortModel}
				onSortModelChange={(newModel) => setSortModel(newModel)}
			/>
			<DevJSONView value={{ selectedRow, rowSelectionModel }} />
			{showShareDialog && (
				<ShareDialog
					title={selectedRow ? 'Config Row' : 'Config'}
					hideUsers={true}
					showAdmin={userDetails?.isFolioAdmin}
					{...(userDetails?.isFolioAdmin && {
						config: { brokerGroup: { groupBy: 'ledger' } },
					})}
					hideLedger={Boolean(selectedRow)}
					onConfirm={async (_userIds, brokerGroupIds, ledger) => {
						await handleShare(brokerGroupIds, ledger);
					}}
				/>
			)}
		</Stack>
	);
};

export default ClientConfigTable;
